import React from 'react';
import SocialIcon from '../../SocialIcon';
import { SOCIAL_LINKS, socialLabel } from '../../../constants/social';
import { Link, useLocation } from 'react-router-dom';
import { BookOpenIcon, KeyIcon } from '@heroicons/react/24/outline';
import { STORY_TAGLINE } from '../../../content/brandLine';


// ============================================================================
// FOOTER V2 (founder-approved variant "A", 2026-08-16): grouped Product /
// Company / Legal / Connect columns.
//
// WIDENED 2026-08-18 (founder: pages linked FROM this footer — Pricing,
// Resources, Contact, Privacy Policy, Student Data Principles, Terms of
// Service — must carry the SAME footer, not SiteFooter's flat 6-link row;
// Student Data Principles was the example caught with only a partial set).
// This superseded the original "V2-scoped on purpose" restriction: this
// footer is now the one footer for every standalone public page. SiteFooter
// still exists (V1 landing, ResourcePaperPage, MajorDoorsPage were left
// untouched — not direct links from this footer) but is no longer the
// default for new public pages — reach for this one.
// The video tours library was retired 2026-09-16 (founder: "remove the
// video tours from within the app"); the landing page carries the one film.
//
//   - THE TEAM LINK LIVES HERE. SiteFooter never carries it, so any page
//     still on SiteFooter has no path to /team until it moves to this one.
//   - "For schools & districts" goes to the dedicated /for-schools page
//     (2026-08-16, founder: a counselor is a different reader), replacing
//     the old #for-schools anchor whose deferred hash scroll
//     runs on mount.
// ============================================================================

// The social profiles and their marks come from one module each
// (constants/social.js, components/SocialIcon.jsx), shared with SiteFooter.

const linkCls = 'inline-flex min-h-[36px] items-center text-sm text-[#A8C5A6] transition-colors hover:text-white';

function FooterGroup({ title, children }) {
    return (
        <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-[#9DB89D]">{title}</div>
            <ul className="space-y-1">{children}</ul>
        </div>
    );
}

// The line under the brand: Stratia's brand line on every page
// (R-29, founder 2026-10-23: it replaced the builder line of #2513). One
// definition, imported, never retyped.
export const FOOTER_TAGLINE = STORY_TAGLINE;

export default function FooterV2() {
    const { pathname } = useLocation();

    const teamHref = '/team';
    // On the landing itself a bare anchor scrolls natively; from any other
    // page the link has to go home first and let the deferred hash scroll
    // find the band.
    const onLanding = pathname === '/';
    // Since 2026-08-16 the schools story is its own page (founder: a
    // counselor is a different reader); the footer sends everyone there.
    const schoolsHref = '/for-schools';

    return (
        <footer data-testid="v2-footer" className="bg-[#0D2818] px-6 py-12 text-white lg:px-8">
            <div className="mx-auto max-w-7xl">
                <div className="flex flex-col gap-10 md:flex-row md:justify-between">
                    {/* Brand + tagline */}
                    <div className="max-w-xs">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#1A4D2E] to-[#2D6B45]">
                                <BookOpenIcon className="absolute h-5 w-5 text-white" style={{ top: '8px', left: '8px' }} />
                                <KeyIcon className="absolute h-4 w-4 text-[#D6E8D5]" style={{ bottom: '6px', right: '6px' }} />
                            </div>
                            <div className="flex items-baseline gap-1">
                                <span className="font-serif text-lg font-semibold">Stratia</span>
                                <span className="text-xs font-medium text-[#A8C5A6]">Admissions</span>
                            </div>
                        </Link>
                        <p className="mt-3 text-sm leading-relaxed text-[#A8C5A6]">
                            {FOOTER_TAGLINE}
                        </p>
                    </div>

                    {/* Grouped links */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-8 sm:grid-cols-4">
                        <FooterGroup title="Product">
                            <li><Link to="/tour" className={linkCls}>Tour</Link></li>
                            <li><Link to="/pricing" className={linkCls}>Pricing</Link></li>
                            <li><Link to="/news" className={linkCls}>News</Link></li>
                            <li><Link to="/resources" className={linkCls}>Resources</Link></li>
                            <li><Link to="/colleges" className={linkCls}>Colleges</Link></li>
                        </FooterGroup>
                        <FooterGroup title="Company">
                            <li>
                                <Link to={teamHref} data-testid="v2-footer-team-link" className={linkCls}>
                                    The Team
                                </Link>
                            </li>
                            <li>
                                {onLanding ? (
                                    <a href={schoolsHref} data-testid="v2-footer-schools-link" className={linkCls}>
                                        For schools &amp; districts
                                    </a>
                                ) : (
                                    <Link to={schoolsHref} data-testid="v2-footer-schools-link" className={linkCls}>
                                        For schools &amp; districts
                                    </Link>
                                )}
                            </li>
                            <li><Link to="/contact" className={linkCls}>Contact</Link></li>
                        </FooterGroup>
                        <FooterGroup title="Legal">
                            <li><Link to="/privacy" className={linkCls}>Privacy Policy</Link></li>
                            <li><Link to="/student-data-principles" className={linkCls}>Student Data Principles</Link></li>
                            <li><Link to="/terms" className={linkCls}>Terms of Service</Link></li>
                            {/* The campus photos' credits (#2944): CC BY and BY-SA ask that a
                                reader can find them, so every public page links the list. */}
                            <li><Link to="/photo-credits" data-testid="v2-footer-photo-credits-link" className={linkCls}>Photo credits</Link></li>
                            {/* Patents page (founder, 2026-09-14): the legal-column
                                convention every SaaS footer follows, so every public page can
                                reach the list of patent applications Stratia has filed. */}
                            <li><Link to="/patents" data-testid="v2-footer-patents-link" className={linkCls}>Patents</Link></li>
                        </FooterGroup>
                        <FooterGroup title="Connect">
                            {SOCIAL_LINKS.map((sl) => (
                                <li key={sl.key}>
                                    <a
                                        href={sl.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={socialLabel(sl)}
                                        data-cta={sl.cta}
                                        className={`${linkCls} gap-2`}
                                    >
                                        <SocialIcon network={sl.key} className="h-5 w-5" />
                                        {sl.name}
                                    </a>
                                </li>
                            ))}
                        </FooterGroup>
                    </div>
                </div>

                <div className="mt-10 border-t border-white/10 pt-8 text-center text-sm text-[#9DB89D]">
                    © {new Date().getFullYear()} Stratia Admissions · San Francisco, CA
                </div>
            </div>
        </footer>
    );
}
